<template>
  <div>
    <main class="container">
      <table-header
        title="Пользователи"
        :is-loading="isLoading"
        :is-empty="!users.length"
      >
        <button
          v-if="canEditUsers"
          class="btn btn-primary-ford-orange modal-ext"
          @click="isAddUserModalShown = true"
        >
          <i class="material-icons">add</i>
          Добавить
        </button>
        <button
          class="btn btn-primary-ford-orange white"
          @click="exportCSV"
        >
          <i class="material-icons">file_download</i>
          Выгрузить в xls
        </button>
        <filter-users
          :role-list="roleListForFilter"
          :can-see-ur-code="canSeeURCode"
          :can-see-gsdb-code="canSeeGSDBCode"
          :can-see-carrier-code="canSeeCarrierCode"
          :can-see-claim-agent-code="canSeeClaimAgentCode"
          @applyFilters="applyFilters"
        />
      </table-header>
      <div class="table-wrapper">
        <div class="table-container">
          <transition
            name="fade"
            mode="out-in"
          >
            <table
              v-if="!isLoading && users.length"
              class="table"
              :class="{'interactive': canEditUsers || userReadOnly}"
            >
              <thead>
                <tr>
                  <th v-if="canEditUsers || userReadOnly">
                    <div class="table__header_setings">
                      <i class="material-icons">tune</i>
                    </div>
                  </th>
                  <th
                    v-for="header in tableHeaders"
                    :key="header.key"
                    :class="[{'sortable sortable_asc': header.sortable}, getSortClass(header.key)]"
                    @click="changeSortKey(header.key, header.sortable)"
                  >
                    <div class="table__header">
                      {{ header.label }}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(it, i) in usersReadable"
                  :key="it.id"
                >
                  <td v-if="canEditUsers || userReadOnly">
                    <user-actions
                      :user="users[i]"
                      :role-list="roleList"
                      @editUser="openEditUserModal"
                      @deleteUser="deleteUser"
                      @blockUser="showBlockUserWarning"
                      @resetPassword="resetUserPassword(it.email)"
                    />
                  </td>
                  <td class="deactivate">
                    <transition
                      name="fade"
                      mode="out-in"
                    >
                      <div
                        v-if="!it.role.length"
                        class="deactivate-user"
                      >
                        <i class="material-icons">lock</i>
                        <span class="deactivate-user__alert">
                          Пользователь заблокирован
                        </span>
                      </div>
                    </transition>
                    {{ it.email }}
                  </td>
                  <td>{{ it.name }}</td>
                  <td>{{ it.phone }}</td>
                  <td>{{ it.role }}</td>
                  <td v-if="canSeeURCode">
                    {{ it.URCodes }}
                  </td>
                  <td v-if="canSeeGSDBCode">
                    {{ it.GSDBCode }}
                  </td>
                  <td v-if="canSeeCarrierCode">
                    {{ it.carrierName }}
                  </td>
                  <td v-if="canSeeClaimAgentCode">
                    {{ it.claimAgentName }}
                  </td>
                </tr>
              </tbody>
            </table>
          </transition>
        </div>
      </div>
      <table-footer
        v-if="users.length && !isLoading"
        :current-page="currentPage"
        :per-page="perPage"
        :limit="limit"
        :total-count="totalCount"
        @pageChanged="(page) => currentPage = page"
        @limitChanged="(lim) => limit = lim"
      />
    </main>
    <add-user-modal
      v-if="user && canEditUsers || userReadOnly"
      v-model="isAddUserModalShown"
      :user="user"
      :role-list="roleListFiltered"
      @addUser="updateUsers"
    />
    <edit-user-modal
      v-if="user && canEditUsers || userReadOnly"
      v-model="isEditUserModalShown"
      :user="user"
      :user-edited="userEdited"
      :role-list="roleList"
      :role-list-filtered="roleListFiltered"
      @editUser="editUser"
    />
    <block-user-warning
      v-model="isBlockUserWarningShown"
      :user="userEdited"
      @blockUser="blockUser"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import TableHeader from '@/components/table/TableHeader';
import TableFooter from '@/components/table/TableFooter';
import AddUserModal from '@/components/users/AddUser';
import EditUserModal from '@/components/users/EditUser';
import BlockUserWarning from '@/components/users/BlockUserWarning';
import FilterUsers from '@/components/users/FilterUsers';
import UserActions from '@/components/users/UserActions';

export default {
  name: 'UserList',
  components: {
    TableHeader,
    TableFooter,
    AddUserModal,
    EditUserModal,
    BlockUserWarning,
    FilterUsers,
    UserActions,
  },
  data: () => ({
    isLoading: true,
    users: [],
    currentPage: 1,
    perPage: 10,
    limit: 10,
    totalCount: null,
    isAddUserModalShown: false,
    isEditUserModalShown: false,
    isBlockUserWarningShown: false,
    isFilterShown: false,
    filters: {},
    pageWatcherDisabled: false,
    userEdited: {},
    roleList: [],
    sortReversed: false,
    sortKey: '',
    sort: '',
  }),
  computed: {
    ...mapState(['user', 'isBackgroundBlurred']),
    pages() {
      return Math.ceil(this.totalCount / this.perPage);
    },
    usersReadable() {
      return this.users.map(it => ({
        id: it.id,
        email: it.email,
        name: it.name,
        phone: it.phone,
        role: it.role.join(', '),
        URCodes: it.URCodes.join(', '),
        GSDBCode: it.GSDBCode,
        isBlocked: it.isBlocked,
        carrierName: it.carrier ? it.carrier.name : '',
        claimAgentName: it.claimAgent ? it.claimAgent.name : '',
      }));
    },
    offset() {
      return this.limit * (this.currentPage - 1);
    },
    roleListForFilter() {
      if (this.user.role.includes('businessSVO')) {
        return this.roleList;
      }

      if (!this.user) {
        return [];
      }

      if (this.userReadOnly) {
        return this.roleList;
      }

      return this.roleList.filter(role => {
        return role.availableFor.filter(roleAvailable => {
          return this.user.role.includes(roleAvailable);
        }).length;
      });
    },
    // Доступные для изменения роли
    roleListFiltered() {
      /*eslint-disable */
      if (!this.user) {
        return [];
      }

      if (this.userReadOnly) {
        return this.roleList;
      }

      return this.roleList.filter(role => {
        return role.availableFor.filter(roleAvailable => {
          return this.user.role.includes(roleAvailable);
        }).length;
      });
    },
    userReadOnly() {
      return this.user.interfaces.includes('UM-SVO-readonly');
    },
    canEditUsers() {
      return this.user.interfaces.includes('UM-user-add');
    },
    canSeeURCode() {
      return this.user.interfaces.includes('UM-column-URCode') || this.user.interfaces.includes('UM-SVO-readonly');
    },
    canSeeGSDBCode() {
      return this.user.interfaces.includes('UM-column-GSDB');
    },
    canSeeCarrierCode() {
      return this.user.interfaces.includes('UM-column-CarrierCode');
    },
    canSeeClaimAgentCode() {
      return this.user.interfaces.includes('UM-column-ClaimAgentCode');
    },
    tableHeaders() {
      const headers = [
        {
          key: 'email',
          label: 'email',
          sortable: true,
        },
        {
          key: 'name',
          label: 'Имя на портале',
          sortable: true,
        },
        {
          key: 'phone',
          label: 'Раб. телефон',
          sortable: false,
        },
        {
          key: 'role',
          label: 'Роль',
          sortable: true,
        },
      ];

      if (this.canSeeURCode) {
        headers.push({
          key: 'URCode',
          label: 'UR',
          sortable: true,
        });
      }
      if (this.canSeeGSDBCode) {
        headers.push({
          key: 'GSDBCode',
          label: 'GSDB',
          sortable: true,
        });
      }
      if (this.canSeeCarrierCode) {
        headers.push({
          key: 'carrierName',
          label: 'Carrier',
          sortable: true,
        });
      }
      if (this.canSeeClaimAgentCode) {
        headers.push({
          key: 'claimAgentName',
          label: 'ClaimAgent',
          sortable: true,
        });
      }

      return headers;
    },
  },
  watch: {
    limit() {
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getUsers();
    },
    currentPage() {
      if (this.pageWatcherDisabled) {
        return;
      }

      this.getUsers();
    },
    isEditUserModalShown(value) {
      this.$store.commit('setBackgroundBlur', value);
    },
    isAddUserModalShown(value) {
      this.$store.commit('setBackgroundBlur', value);
    },
  },
  created() {
    this.getUsers();
    this.getRoles();
  },
  methods: {
    async getUsers() {
      this.isLoading = true;
      const { items, totalCount } = await this.$http.get('users/1.0/users', {
        params: {
          limit: this.limit,
          skip: this.offset,
          sort: this.sort,
          ...this.filters,
        },
      });

      this.isLoading = false;

      this.users = items;
      this.totalCount = totalCount;
      this.perPage = this.limit;
      this.pageWatcherDisabled = false;

      return Promise.resolve();
    },
    async exportCSV() {
      const data = await this.$http.get('users/1.0/users', {
        params: {
          CSV: true,
          sort: this.sort,
          ...this.filters,
        },
      });

      const blob = new Blob([data], { type: 'text/csv' });

      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = 'users.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    async updateUsers() {
      this.filters = {};
      this.pageWatcherDisabled = true;
      this.currentPage = 1;

      await this.getUsers();

      this.isAddUserModalShown = false;
    },
    async resetUserPassword(email) {
      this.$bvToast.toast('Пожалуйста, подождите', {
        title: 'Запрос отправлен',
        variant: 'warning',
        solid: true,
        autoHideDelay: 3000,
      });
      await this.$http.post(`users/1.0/reset-password/${email}`);
      this.$bvToast.toast('Письмо со ссылкой на восстановление пароля было успешно отправлено пользователю.', {
        title: 'Готово!',
        variant: 'success',
        solid: true,
        autoHideDelay: 15000,
      });
    },
    async deleteUser(id) {
      this.isLoading = true;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;

      await this.$http.delete(`users/1.0/users/${id}`).catch(() => {
        this.isLoading = false;
      });
      await this.getUsers();
    },
    async editUser() {
      await this.getUsers();
      this.isEditUserModalShown = false;
    },
    applyFilters(filters) {
      this.filters = filters;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getUsers();
    },
    showBlockUserWarning(user) {
      this.isBlockUserWarningShown = true;
      this.userEdited = user;
    },
    openEditUserModal(user) {
      this.isEditUserModalShown = true;
      this.userEdited = user;
    },
    async getRoles() {
      /* eslint-disable */
      const { items } = await this.$http.get('users/1.0/roles');
      this.roleList = items;
    },
    async blockUser(userId) {
      await this.$http.post(`users/1.0/users/${userId}/block`).finally(() => {
        this.isBlockUserWarningShown = false;
      });

      const user = this.users.find(it => it.id === userId);
      this.$set(user, 'role', []);
    },
    changeSortKey(sortKey, sortable) {
      if (this.isLoading || !sortable) {
        return;
      }

      this.sortReversed = this.sortKey === sortKey ? !this.sortReversed : false;
      this.sortKey = sortKey;

      this.sort = (this.sortReversed ? '-' : '+') + this.sortKey;
      this.pageWatcherDisabled = true;
      this.currentPage = 1;
      this.getUsers();
    },
    getSortClass(sortKey) {
      return { [`sortable_active ${this.sortReversed ? 'sortable_desc' : 'sortable_asc'}`]: this.sortKey === sortKey };
    },
  },
};
</script>
